import { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";

const SearchableDropdown = ({ options, value, onChange, placeholder = "Select...", inline = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuStyle, setMenuStyle] = useState({});
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const menuRef = useRef(null);

  const selectedLabel = options.find((o) => o.value === value)?.label || placeholder;
  const defaultValue = options.length > 0 ? options[0].value : "";
  const hasValue = value !== defaultValue && value !== null && value !== undefined;

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = useCallback(
    (optionValue) => {
      onChange(optionValue);
      setIsOpen(false);
      setSearch("");
    },
    [onChange],
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange(defaultValue);
      setIsOpen(false);
      setSearch("");
    },
    [onChange, defaultValue],
  );

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
    setSearch("");
  }, []);

  useEffect(() => {
    if (isOpen && containerRef.current && !inline) {
      const rect = containerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen, inline]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus({ preventScroll: true });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="uk-inline searchable-dropdown" ref={containerRef}>
      <button
        className="uk-button uk-button-default"
        type="button"
        onClick={handleToggle}
      >
        <span className="searchable-dropdown-label">{selectedLabel}</span>
        <span className="searchable-dropdown-icons">
          {hasValue && (
            <span
              className="searchable-dropdown-clear"
              onClick={handleClear}
              uk-icon="icon: close; ratio: 0.7"
            ></span>
          )}
          <span uk-icon="icon: chevron-down"></span>
        </span>
      </button>
      {isOpen && (() => {
        const menu = (
          <div
            className="searchable-dropdown-menu"
            ref={menuRef}
            style={inline ? undefined : menuStyle}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="searchable-dropdown-search">
              <input
                ref={searchInputRef}
                type="text"
                className="uk-input"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
              />
            </div>
            <ul className="searchable-dropdown-list">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <li
                    key={option.value}
                    className={value === option.value ? "active" : ""}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </li>
                ))
              ) : (
                <li className="no-results">No results found</li>
              )}
            </ul>
          </div>
        );
        return inline ? menu : ReactDOM.createPortal(menu, document.body);
      })()}
    </div>
  );
};

export default SearchableDropdown;
